import React, { useEffect, useState } from "react";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
import { useGetSupportIssuesMutation, useResolveSupportIssueMutation } from "../../../features/tenant/userPermissions/userPermissions";
import { toast } from "react-hot-toast";

const HelpAndSupport = () => {
    const [issues, setIssues] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [limit, setLimit] = useState(10);
    const [offset, setOffset] = useState(0);

    // Remarks modal state
    const [showRemarksModal, setShowRemarksModal] = useState(false);
    const [selectedIssueId, setSelectedIssueId] = useState(null);
    const [remarks, setRemarks] = useState("");

    const [getSupportIssues, { isLoading: isFetching }] = useGetSupportIssuesMutation();
    const [resolveSupportIssue, { isLoading: isResolving }] = useResolveSupportIssueMutation();

    const fetchIssues = async () => {
        try {
            const res = await getSupportIssues({
                token: sessionStorage.getItem("access_token"),
                tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
            });
            if (res.data && res.data.success) {
                setIssues(res.data.body);
                setTotalRows(res.data.body.length);
            } else {
                toast.error(res?.data?.message || "Failed to fetch issues");
            }
        } catch (error) {
            console.error("Error fetching issues:", error);
            toast.error("Something went wrong while fetching issues");
        }
    };

    useEffect(() => {
        fetchIssues();
    }, []);

    const openRemarksModal = (id) => {
        setSelectedIssueId(id);
        setRemarks("");
        setShowRemarksModal(true);
    };

    const handleResolve = async () => {
        if (!selectedIssueId) return;
        try {
            const res = await resolveSupportIssue({
                token: sessionStorage.getItem("access_token"),
                tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
                data: { id: selectedIssueId, remarks: remarks.trim() || null },
            });
            if (res.data && res.data.success) {
                toast.success("Issue marked as resolved");
                setShowRemarksModal(false);
                setSelectedIssueId(null);
                setRemarks("");
                fetchIssues();
            } else {
                toast.error(res?.data?.message || "Failed to resolve issue");
            }
        } catch (error) {
            console.error("Error resolving issue:", error);
            toast.error("Something went wrong while resolving the issue");
        }
    };

    const columns = [
        {
            name: "SNo",
            cell: (row, idx) => offset + Number(idx + 1),
            sortable: true,
            width: "70px",
        },
        {
            name: "App User ID",
            cell: (row) => row.app_user_id,
            sortable: true,
            width: "100px",
        },
        {
            name: "Name",
            cell: (row) => row.user_name || "N/A",
            sortable: true,
            width: "130px",
        },
        {
            name: "Phone",
            cell: (row) => row.user_mobile || "N/A",
            sortable: true,
            width: "120px",
        },
        {
            name: "Email",
            cell: (row) => row.user_email || "N/A",
            sortable: true,
            width: "170px",
        },
        {
            name: "Address",
            cell: (row) => (
                <div title={row.user_address || ""} className="truncate">
                    {row.user_address || "N/A"}
                </div>
            ),
            sortable: true,
            width: "160px",
        },
        {
            name: "Category",
            cell: (row) => row.message,
            sortable: true,
            width: "150px",
        },
        {
            name: "Description",
            cell: (row) => (
                <div title={row.support_text} className="truncate">
                    {row.support_text}
                </div>
            ),
            sortable: true,
            width: "200px",
        },
        {
            name: "Status",
            cell: (row) => (
                <span className={`px-2 py-1 rounded text-xs font-semibold ${row.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                    {row?.status?.toUpperCase() || "N/A"}
                </span>
            ),
            sortable: true,
            width: "110px",
        },
        {
            name: "Remarks",
            cell: (row) => (
                <div title={row.remarks || ""} className="truncate">
                    {row.remarks || "-"}
                </div>
            ),
            sortable: true,
            width: "160px",
        },
        {
            name: "Created At",
            cell: (row) => new Date(row.created_at).toLocaleString(),
            sortable: true,
            width: "170px",
        },
        {
            name: "Action",
            cell: (row) => (
                row.status === "pending" ? (
                    <button
                        onClick={() => openRemarksModal(row.id)}
                        disabled={isResolving}
                        className="bg-green-600 text-white px-3 py-1 rounded-lg text-sm hover:bg-green-700 disabled:opacity-50"
                    >
                        Resolve
                    </button>
                ) : (
                    <span className="text-gray-500 italic">Resolved</span>
                )
            ),
            width: "120px",
        },
    ];

    return (
        <div className="p-4 sm:p-6 md:p-8 bg-gray-50 min-h-screen">
            <div className="mb-6">
                <LinksComponent />
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-white">
                    <h1 className="text-2xl font-bold text-gray-800">Help and Support Issues</h1>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={fetchIssues}
                            className="p-2 text-gray-500 hover:text-blue-600 transition-colors"
                            title="Refresh"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                        </button>
                    </div>
                </div>

                <DataTableComponent
                    columns={columns}
                    data={issues}
                    pagination
                    paginationTotalRows={totalRows}
                    progressPending={isFetching}
                    onChangeRowsPerPage={(newLimit) => setLimit(newLimit)}
                    onChangePage={(newPage) => setOffset((newPage - 1) * limit)}
                />
            </div>

            {/* Remarks Modal */}
            {showRemarksModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-6 relative">
                        <button
                            onClick={() => {
                                setShowRemarksModal(false);
                                setSelectedIssueId(null);
                                setRemarks("");
                            }}
                            className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 text-xl"
                        >
                            ✕
                        </button>
                        <h2 className="text-lg font-bold text-gray-800 mb-4">Resolve Issue</h2>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Remarks (optional)
                            </label>
                            <textarea
                                value={remarks}
                                onChange={(e) => setRemarks(e.target.value)}
                                rows={4}
                                placeholder="Enter remarks for resolving this issue..."
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                            />
                        </div>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setShowRemarksModal(false);
                                    setSelectedIssueId(null);
                                    setRemarks("");
                                }}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleResolve}
                                disabled={isResolving}
                                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
                            >
                                {isResolving ? "Resolving..." : "Mark Resolved"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HelpAndSupport;